'use client';
import { useRef, useState, useEffect, useCallback } from 'react';
import SafeImage from './SafeImage';

interface ZoomableImageProps {
  src: string;
  fallbackSrc?: string;
  blurDataURL?: string;
  alt: string;
  onImageChange?: () => void;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  className?: string;
}

interface TouchState {
  distance: number;
  centerX: number;
  centerY: number;
}

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_DELAY = 300;

export default function ZoomableImage({
  src,
  fallbackSrc,
  blurDataURL,
  alt,
  onImageChange,
  onLoad,
  className = '',
}: ZoomableImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isPinching, setIsPinching] = useState(false);
  const lastTapRef = useRef(0);
  const touchStateRef = useRef<TouchState | null>(null);
  const lastPositionRef = useRef({ x: 0, y: 0 });
  const initialTouchRef = useRef<{ x: number; y: number; time: number } | null>(
    null
  );
  const hasDraggedRef = useRef(false);

  // Reset zoom when image changes
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    onImageChange?.();
  }, [src, onImageChange]);

  // Calculate distance between two touches
  const getDistance = (touch1: Touch, touch2: Touch): number => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Calculate center point between two touches
  const getCenter = (touch1: Touch, touch2: Touch) => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  };

  // Constrain position to keep image within bounds
  const constrainPosition = useCallback(
    (newX: number, newY: number, newScale: number) => {
      if (!containerRef.current || !imageRef.current) return { x: 0, y: 0 };

      const container = containerRef.current;
      const image = imageRef.current;

      const containerRect = container.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();

      const scaledWidth = imageRect.width * newScale;
      const scaledHeight = imageRect.height * newScale;

      const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
      const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);

      return {
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY)),
      };
    },
    []
  );

  // Handle double tap to zoom
  const handleDoubleTap = useCallback(
    (touchX: number, touchY: number) => {
      const now = Date.now();
      const timeSinceLastTap = now - lastTapRef.current;

      if (timeSinceLastTap < DOUBLE_TAP_DELAY && timeSinceLastTap > 0) {
        // Double tap detected
        if (scale > MIN_SCALE) {
          // Reset zoom
          setScale(MIN_SCALE);
          setPosition({ x: 0, y: 0 });
        } else {
          // Zoom to 2x at the tap location
          const newScale = 2;
          if (containerRef.current) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const centerX =
              touchX - containerRect.left - containerRect.width / 2;
            const centerY =
              touchY - containerRect.top - containerRect.height / 2;
            setScale(newScale);
            setPosition({
              x: -centerX * (newScale - 1),
              y: -centerY * (newScale - 1),
            });
          } else {
            setScale(newScale);
            setPosition({ x: 0, y: 0 });
          }
        }
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
    },
    [scale]
  );

  // Handle touch start
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        // Single touch - prepare for drag or double tap
        // Only handle if zoomed, otherwise let parent handle swipe
        if (scale <= MIN_SCALE) {
          return; // Don't interfere with parent swipe handlers when not zoomed
        }
        const touch = e.touches[0];
        const now = Date.now();
        initialTouchRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          time: now,
        };
        lastPositionRef.current = { x: touch.clientX, y: touch.clientY };
        setIsDragging(scale > MIN_SCALE);
      } else if (e.touches.length === 2) {
        // Two touches - pinch to zoom
        e.preventDefault();
        setIsPinching(true);
        setIsDragging(false);
        const touch1 = e.touches[0] as Touch;
        const touch2 = e.touches[1] as Touch;
        const distance = getDistance(touch1, touch2);
        const center = getCenter(touch1, touch2);

        if (!containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        touchStateRef.current = {
          distance,
          centerX: center.x - containerRect.left - containerRect.width / 2,
          centerY: center.y - containerRect.top - containerRect.height / 2,
        };
      }
    },
    [scale, handleDoubleTap]
  );

  // Handle touch move
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1 && isDragging && scale > MIN_SCALE) {
        // Single touch drag when zoomed
        e.preventDefault();
        const touch = e.touches[0] as Touch;
        const deltaX = touch.clientX - lastPositionRef.current.x;
        const deltaY = touch.clientY - lastPositionRef.current.y;

        const newX = position.x + deltaX;
        const newY = position.y + deltaY;

        const constrained = constrainPosition(newX, newY, scale);
        setPosition(constrained);

        lastPositionRef.current = { x: touch.clientX, y: touch.clientY };
      } else if (e.touches.length === 2 && isPinching) {
        // Two touches - pinch to zoom
        e.preventDefault();
        const touch1 = e.touches[0] as Touch;
        const touch2 = e.touches[1] as Touch;
        const distance = getDistance(touch1, touch2);

        if (touchStateRef.current) {
          const scaleChange = distance / touchStateRef.current.distance;
          const newScale = Math.max(
            MIN_SCALE,
            Math.min(MAX_SCALE, scale * scaleChange)
          );

          if (!containerRef.current) return;

          const containerRect = containerRef.current.getBoundingClientRect();
          const center = getCenter(touch1, touch2);
          const centerX =
            center.x - containerRect.left - containerRect.width / 2;
          const centerY =
            center.y - containerRect.top - containerRect.height / 2;

          // Adjust position to zoom towards the pinch center
          const scaleDiff = newScale - scale;
          const newX =
            position.x -
            (centerX - position.x) * (scaleDiff / scale) +
            (touchStateRef.current.centerX - centerX);
          const newY =
            position.y -
            (centerY - position.y) * (scaleDiff / scale) +
            (touchStateRef.current.centerY - centerY);

          const constrained = constrainPosition(newX, newY, newScale);
          setScale(newScale);
          setPosition(constrained);

          touchStateRef.current.distance = distance;
          touchStateRef.current.centerX = centerX;
          touchStateRef.current.centerY = centerY;
        }
      }
    },
    [isDragging, isPinching, scale, position, constrainPosition]
  );

  // Handle touch end
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 0 && initialTouchRef.current) {
        // Check if this was a tap (not a drag)
        const touch = e.changedTouches[0];
        const moveDistance = Math.sqrt(
          Math.pow(touch.clientX - initialTouchRef.current.x, 2) +
            Math.pow(touch.clientY - initialTouchRef.current.y, 2)
        );
        const timeDiff = Date.now() - initialTouchRef.current.time;

        // If it was a quick tap with minimal movement, check for double tap
        if (moveDistance < 10 && timeDiff < 300) {
          handleDoubleTap(touch.clientX, touch.clientY);
        }
      }

      setIsDragging(false);
      setIsPinching(false);
      touchStateRef.current = null;
      initialTouchRef.current = null;

      // Snap back to min scale if zoomed out too far
      if (scale < MIN_SCALE) {
        setScale(MIN_SCALE);
        setPosition({ x: 0, y: 0 });
      }
    },
    [scale, handleDoubleTap]
  );

  // Handle mouse wheel zoom (for desktop)
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.max(
          MIN_SCALE,
          Math.min(MAX_SCALE, scale * delta)
        );

        if (!containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const centerX =
          e.clientX - containerRect.left - containerRect.width / 2;
        const centerY =
          e.clientY - containerRect.top - containerRect.height / 2;

        const scaleDiff = newScale - scale;
        const newX = position.x - (centerX - position.x) * (scaleDiff / scale);
        const newY = position.y - (centerY - position.y) * (scaleDiff / scale);

        const constrained = constrainPosition(newX, newY, newScale);
        setScale(newScale);
        setPosition(constrained);
      }
    },
    [scale, position, constrainPosition]
  );

  // Handle click to zoom (for desktop)
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Only handle clicks if we didn't drag
      if (hasDraggedRef.current) {
        hasDraggedRef.current = false;
        return;
      }

      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const centerX = e.clientX - containerRect.left - containerRect.width / 2;
      const centerY = e.clientY - containerRect.top - containerRect.height / 2;

      if (scale > MIN_SCALE) {
        // Reset zoom
        setScale(MIN_SCALE);
        setPosition({ x: 0, y: 0 });
      } else {
        // Zoom to 2x at the click location
        const newScale = 2;
        setScale(newScale);
        setPosition({
          x: -centerX * (newScale - 1),
          y: -centerY * (newScale - 1),
        });
      }
    },
    [scale]
  );

  // Handle mouse drag (for desktop)
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button === 0) {
        // Reset drag tracking
        hasDraggedRef.current = false;

        if (scale > MIN_SCALE) {
          setIsDragging(true);
          lastPositionRef.current = { x: e.clientX, y: e.clientY };
        }
      }
    },
    [scale]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && scale > MIN_SCALE) {
        // Mark that we've dragged
        hasDraggedRef.current = true;

        const deltaX = e.clientX - lastPositionRef.current.x;
        const deltaY = e.clientY - lastPositionRef.current.y;

        const newX = position.x + deltaX;
        const newY = position.y + deltaY;

        const constrained = constrainPosition(newX, newY, scale);
        setPosition(constrained);

        lastPositionRef.current = { x: e.clientX, y: e.clientY };
      }
    },
    [isDragging, scale, position, constrainPosition]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Prevent context menu only when actively interacting (dragging/pinching)
  // This allows desktop right-click while preventing interference during zoom/pan
  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      // Only prevent if user is actively dragging or pinching
      if (isDragging || isPinching) {
        e.preventDefault();
      }
      // Otherwise, allow default context menu (right-click) on desktop
    },
    [isDragging, isPinching]
  );

  return (
    <div
      ref={containerRef}
      className={`relative h-full w-full touch-none overflow-hidden ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
    >
      <div
        ref={imageRef}
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          transformOrigin: 'center center',
          transition:
            isDragging || isPinching ? 'none' : 'transform 0.2s ease-out',
          willChange: scale > MIN_SCALE ? 'transform' : 'auto',
        }}
      >
        <SafeImage
          src={src}
          fallbackSrc={fallbackSrc}
          blurDataURL={blurDataURL}
          alt={alt}
          draggable={false}
          skipOptimize
          fill
          className="object-contain"
          onLoad={onLoad}
        />
      </div>
    </div>
  );
}
